import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { observer, inject } from 'mobx-react'
import { extendObservable } from 'mobx'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import yup from 'yup'
import startsWith from 'lodash/startsWith'
import merge from 'lodash/merge'
import get from 'lodash/get'
import axios, { capi } from '@atheneum/api'

import { precision } from '@atheneum/api/dist/lib'

import FeeFormBody from './FeeFormBody'
import Spinner from '@components/spinner'
import { validateProject } from '../helpers/fee.helpers.js'

import FeeFormStore from './feeForm.store'

class FeeForm extends Component {
  static propTypes = {
    staticData: PropTypes.object,
    feeStore: PropTypes.object,
    modalStore: PropTypes.object,
    growlStore: PropTypes.object,
    ruleStore: PropTypes.object,
    deliverableStore: PropTypes.object,
    fee: PropTypes.object,
    project: PropTypes.object,
    location: PropTypes.object
  }

  static defaultProps = {
    epl: true,
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: false
  }

  state = { error: null }

  constructor(props) {
    super(props)
    extendObservable(this, {
      feeFormStore: new FeeFormStore(props.staticData, props.fee, props.project)
    })
  }


  getInitialForm() {
    const { ruleStore, fee } = this.props
    let deliveryDate = fee.deliveryDate
    const isFeeCreateForm = !fee.id
    if (isFeeCreateForm && !ruleStore.isBackdatingRuleSatisfied(deliveryDate)) {
      deliveryDate = null
    }
    const feeTypeId = this.calcFeeTypeId(fee.eplStatusId)

    const projectId = this.feeFormStore.project.id

    this.validateProjectId(projectId)

    return {
      ...fee,
      contractTypeId: get(fee, 'contract.contractTypeId', null),
      contractId: Number(get(fee, 'contractId', null)),
      deliveryDate,
      projectId,
      feeTypeId,
      deliverableAttach: isFeeCreateForm ? 'new' : 'existing',
      deliverableId: isFeeCreateForm ? null : fee.deliverableId,
      freeCallFeeId: isFeeCreateForm ? null : fee.freeCallFeeId,
      freeCallCostTypeId: isFeeCreateForm ? null : fee.freeCallCostTypeId
    }
  }

  /**
   * We had a couple of issues which caused wrong project IDs to be saved.
   * The
   */
  validateProjectId = projectId => {
    const { location, growlStore, modalStore } = this.props

    const extractProjectIdFromUrlRegex = /\/project\/(\d+)/
    const [, projectIdInUrl] = extractProjectIdFromUrlRegex.exec(
      location.pathname
    )

    if (parseInt(projectId, 10) !== parseInt(projectIdInUrl, 10)) {
      const projectIdError = new Error(
        `Fee project error - Project ID un-synced, this is caused by a race condition on the frontend,
project ID in url: ${projectIdInUrl}
project ID in store: ${projectId}`
      )

      growlStore.showError(projectIdError)
      modalStore.close()
    }
  }

  /**
   * Calculate default feeType based on epl statusId.
   * If epl is in status Canceled it should be 'Cancellation Fee'.
   * If epl is in status Interviewed it should be 'Base Fee'.
   * @param {Number} eplStatusId
   * @returns {Number}
   */
  calcFeeTypeId(eplStatusId) {
    const { staticData, fee } = this.props
    const { CONST } = staticData
    const currentValue = fee.feeTypeId

    if (currentValue) return currentValue

    return eplStatusId === CONST.EPL_STATUSES.NOT_INTERVIEWED
      ? CONST.FEE_TYPES.CANCELLATION_RESCHEDULING
      : CONST.FEE_TYPES.BASE_FEE
  }

  getValidationSchema() {
    const { staticData, fee } = this.props
    const { costTypes } = staticData.names
    const { CONTRACT_TYPES } = staticData.CONST

    const numberRegex = /^[-]?[0-9]+([.][0-9]+)?$/
    const invalidNumberMessage =
      'Please use dot (.) as a decimal separator and enter a valid number.'

    const feeSchemaPart = {
      feeTypeId: yup
        .number()
        .integer()
        .nullable()
        .required('Fee type is required'),
      freeCall: yup.boolean().nullable(),
      freeCallCostTypeId: yup
        .number()
        .integer()
        .nullable()
        .when('freeCall', (freeCall, schema) =>
          freeCall ? schema.required('Free call cost type is required') : schema),
      deliveredBy: yup
        .number()
        .integer()
        .nullable(),
      feeCurrencyId: yup
        .number()
        .integer()
        .nullable()
        .when('contractTypeId', (contractTypeId, schema) => {
          if (!contractTypeId || contractTypeId === CONTRACT_TYPES.PAYG) {
            return schema.required(
              'Fee currency is required for PAYG contracts'
            )
          } else {
            return schema
          }
        }),
      feeValue: yup
        .string()
        .nullable()
        .required('Fee value is required')
        .matches(numberRegex, invalidNumberMessage)
        .test(
          'two-decimal-places',
          'Number can contain only 2 decimal places. (e.g. 33.20)',
          precision(2)
        ),
      credits: yup
        .string()
        .nullable()
        .when('contractTypeId', (contractTypeId, schema) => {
          if (contractTypeId && contractTypeId !== CONTRACT_TYPES.PAYG) {
            return schema.required(
              'Credits are required for PAC and SUB contracts'
            )
          } else {
            return schema
          }
        })
        .matches(numberRegex, invalidNumberMessage)
        .test(
          'six-decimal-places',
          'Credits can contain up to 6 decimal places. (e.g. 0.123456)',
          precision(6)
        ),
      contractId: yup
        .number()
        .integer()
        .nullable()
        .required('Contract is required'),
      attachments: yup.array().of(yup.object()).nullable()
    }

    const costSchemaPart = {
      costTypeId: yup
        .number()
        .integer()
        .nullable()
        .required('Cost type is required'),
      expert: yup
        .object()
        .nullable()
        .when('costTypeId', (costTypeId, schema) => {
          const costType = costTypes[costTypeId]

          if (startsWith(costType, 'Expert')) {
            return yup.object().typeError('Expert is required')
          } else {
            return schema
          }
        }),
      entityName: yup
        .string()
        .nullable()
        .when('costTypeId', (costTypeId, schema) => {
          const costType = costTypes[costTypeId]

          if (startsWith(costType, 'Third')) {
            return schema.required('Entity name is required')
          } else {
            return schema
          }
        }),
      invoiceNumber: yup.string().nullable(),
      costCurrencyId: yup
        .number()
        .integer()
        .nullable()
        .required('Cost currency is required'),
      costValue: yup
        .string()
        .nullable()
        .required('Cost value is required')
        .matches(numberRegex, invalidNumberMessage)
        .test(
          'two-decimal-places',
          'Number can contain only 2 decimal places. (e.g. 33.20)',
          precision(2)
        ),
      isBillableToClient: yup.boolean().nullable().notRequired()
    }

    const feeCostSchemaPart = {
      duration: yup
        .number()
        .integer()
        .nullable()
        .required('Duration is required')
        .test(
          'only-positive-number',
          'Duration can only be positive. (e.g. 60)',
          (value) => value > 0
        )
    }

    let schemaObject = {
      type: yup.string(),
      deliverableAttach: yup.string(),
      deliveredBy: yup
        .number()
        .typeError('Employee that delivered expert is required')
        .integer()
        .typeError('Employee that delivered expert is required')
        .required('Employee that delivered expert is required'),
      contractTypeId: yup.number().integer().nullable(),
      deliverableType: yup
        .number()
        .integer()
        .nullable()
        .when('deliverableAttach', (deliverableAttach, schema) => {
          if (deliverableAttach === 'new') {
            return schema.required('Deliverable type is required')
          } else {
            return schema
          }
        }),
      deliverableId: yup
        .number()
        .integer()
        .nullable()
        .when('deliverableAttach', (deliverableAttach, schema) => {
          if (deliverableAttach === 'existing') {
            return schema.required('Deliverable is required')
          } else {
            return schema
          }
        }),
      deliveryDate: yup.date().nullable().required('Delivery date is required'),
      duration: yup.number().integer().nullable(),
      description: yup.string().nullable().notRequired()
    }

    switch (fee.type) {
      case 'FEE':
        return yup.object().shape(merge(schemaObject, feeSchemaPart))
      case 'COST':
        return yup.object().shape(merge(schemaObject, costSchemaPart))
      case 'FEECOST':
        return yup
          .object()
          .shape(merge(schemaObject, feeSchemaPart, costSchemaPart, feeCostSchemaPart))
      default:
        throw new Error('invalid fee type')
    }
  }

  getTitle(values) {
    const type = values.type
    return `${values.id ? 'Edit ' : 'Create new'}
      ${type === 'FEE' ? 'Client Charge' : ''}
      ${type === 'COST' ? 'Cost' : ''}
      ${type === 'FEECOST' ? 'Hon. & Fee Bundle' : ''}`
  }

  handleCreateInvoice = async (fee, project, isCallOnly = false) => {
    const { growlStore } = this.props
    growlStore.showSuccess('Creating or updating invoice ...')

    try {
      await capi.createInvoice({
        fee: {
          id: fee.id,
          invoiceId: fee.invoiceId,
          project: {
            id: fee.project.id,
            capiProjectType: fee.project.capiProjectType,
            invoicingEntityId: fee.project.invoicingEntityId,
            caseCode: fee.project.caseCode,
            invoicingRegionId: project.office.address.country.invoicingRegionId
          },
          feeItems: fee.feeItems.map(feeItem => ({
            feeTypeId: feeItem.feeTypeId,
            currencyId: feeItem.currencyId,
          })
          )
        },
        callOnlyInvoice: isCallOnly ? '1' : '0'
      })
      growlStore.showSuccess('Successfully created invoice...')
    } catch (error) {
      growlStore.showError(`Error creating fee and invoice: ${JSON.stringify(error)}`)
    }
  }

  handleCAPIInvoiceCreate = async (fee) => {
    const { growlStore, project } = this.props
    try {
      const call = (await axios({
        method: 'get',
        url: `${process.env.REACT_APP_CAPI_SERVICE_URL}/calls/check?eplId=${fee.expertProjectLinkId}`,
      })).data

      let isCAPIProject = false
      try {
        const capiResponse = (await axios({
          method: 'get',
          url: `${process.env.REACT_APP_CAPI_SERVICE_URL}/check?p=${project.id}`,
        })).data

        isCAPIProject = !!capiResponse.project
      } catch (error) {
        console.error(error)
      }

      if (isCAPIProject && (project.invoicingEntityId || project.capiProjectType)) {
        if (call && call.mck_call_id && call.status === 'completed') {
          await this.handleCreateInvoice(fee, project, true)
          return
        }

        const MC_KINSEY_PARENT_ACCOUNT_ID = 32
        if (isCAPIProject && project.parentAccountId === MC_KINSEY_PARENT_ACCOUNT_ID) {
          await this.handleCreateInvoice(fee, project, false)
        }
      }

    } catch (error) {
      growlStore.showError(error)
    }
  }

  handleFormSubmission = async (values, actions) => {
    this.setState({ error: null })

    const { feeFormStore } = this
    const { feeStore, modalStore, growlStore, deliverableStore, project } = this.props
    const { eplModel } = feeStore
    const feeData = values

    const {
      isProjectInHardcodedList,
      isProjectAfterCutOffDate,
      isValid,
      errMsg
    } = await validateProject(project, feeData);

    if (!isValid) {
      modalStore.close()
      growlStore.showError(errMsg);
      return
    }

    feeFormStore
      .save({ ...feeData })
      .then(async (fee) => {
        if (!isProjectInHardcodedList && isProjectAfterCutOffDate) {
          await this.handleCAPIInvoiceCreate(fee)
        }

        eplModel && eplModel.get()
        modalStore.close()
        growlStore.addNotification({
          message: `${this.getTitle(values)} succeeded.`,
          autoClose: true,
          title: 'Saved',
          type: 'success'
        })

        deliverableStore.get(feeFormStore.project.id)

        if (feeFormStore.fee.id) {
          feeStore.get(feeFormStore.fee.id)
        }
      })
      .catch(err => {
        growlStore.showError(err)

        this.setState({ error: err.message })
        actions.setSubmitting(false)
      })
  }

  render() {
    const { feeStore } = this.props

    if (this.feeFormStore.loading) return <Spinner />
    return (
      <div>
        <Formik
          initialValues={this.getInitialForm()}
          validationSchema={this.getValidationSchema()}
          onSubmit={this.handleFormSubmission}
          render={form => (
            <FeeFormBody
              form={form}
              contracts={this.contracts}
              formError={this.state.error}
              feeStore={feeStore}
              feeFormStore={this.feeFormStore}
            />
          )}
        />
      </div>
    )
  }
}

export default withRouter(
  inject(
    'staticData',
    'ruleStore',
    'modalStore',
    'growlStore',
    'deliverableStore'
  )(observer(FeeForm))
)
